import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { OnboardingFormData } from '../../types';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Key, Building, User, Mail, Phone, CheckCircle2, AlertCircle, Loader2, ArrowLeft, Copy, Eye, EyeOff, Info } from "lucide-react";
import { WhatsAppIcon } from '../../components/icons/WhatsAppIcon';
import authService from '../../services/authService';
import twilioService from '../../services/twilioService';
import { cn } from "@/lib/utils";
import WhatsAppVerification from './WhatsAppVerification';
import AgentNumberManager, { useAgentNumbers } from '../agents/AgentNumberManager';
import whatsappService, { WhatsAppMode, whatsappConnectErrorMessage } from '../../services/whatsappService';

interface ConfirmationStepProps {
  formData: OnboardingFormData;
  onFormChange: (data: Partial<OnboardingFormData>) => void;
  onBack: () => void;
}

export const ConfirmationStep: React.FC<ConfirmationStepProps> = ({ formData, onFormChange, onBack }): JSX.Element => {
  const navigate = useNavigate();
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [, setConnectionStatus] = useState<'idle' | 'connecting' | 'success' | 'error' | 'disconnected'>('idle');
  const [whatsappConnected, setWhatsappConnected] = useState<boolean>(false);
  // Agent numbers + salons, shared with the phone step and the company profile.
  const agentNumbers = useAgentNumbers();
  const [waMode, setWaMode] = useState<WhatsAppMode>('central');
  const [signupData, setSignupData] = useState<any>(null);
  const [authCode, setAuthCode] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error' | 'info', message: string } | null>(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState<boolean>(false);
  const [isCompletingOnboarding, setIsCompletingOnboarding] = useState<boolean>(false);
  const [fbPopupOpen, setFbPopupOpen] = useState<boolean>(false);
  const [popupOpenedAt, setPopupOpenedAt] = useState<number | null>(null);
  const [popupStuck, setPopupStuck] = useState<boolean>(false);
  const isCheckingStatusRef = useRef(false);
  const autoCompleteTriggeredRef = useRef(false);
  
  const [showApiKey, setShowApiKey] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [, setVerificationCode] = useState<string>('');

  const plannedWhatsappNumber = formData.whatsappUseReservedNumber === true
    ? formData.twilioPurchasedNumber
    : formData.whatsappUseReservedNumber === false
      ? (formData.ownWhatsappNumber || 'Eigene Nummer (Eingabe im WhatsApp-Popup)')
      : undefined;

  const handleVerificationCodeReceived = useCallback((code: string) => {
    setVerificationCode(code);
    setStatusMessage({ type: 'success', message: `Verifizierungscode empfangen: ${code}` });
  }, []);

  const handleCheckStatus = useCallback(async () => {
    if (isCheckingStatusRef.current) return;

    try {
      setIsCheckingStatus(true);
      isCheckingStatusRef.current = true;

      // The logged-in salon's own connection (login token). It used to be
      // looked up by e-mail without a login — anybody could read any salon's.
      const status = await whatsappService.getConnectivity();

      if (status.is_connected) {
        const webhookStatus = status.whatsapp_profile?.webhook_configured ? 'Konfiguriert' : 'Manuelle Einrichtung erforderlich';
        setWhatsappConnected(true);
        setConnectionStatus('success');
        setStatusMessage({
          type: 'success',
          message: `WhatsApp erfolgreich verbunden. Nummer: ${status.whatsapp_number || 'N/A'}. Webhook: ${webhookStatus}.`
        });
      } else {
        setWhatsappConnected(false);
        setConnectionStatus('disconnected');
        setStatusMessage({
          type: 'info',
          message: 'WhatsApp ist nicht verbunden. Bitte verbinden Sie sich über Facebook.'
        });
      }
    } catch (error) {
      setConnectionStatus('error');
      setStatusMessage({ type: 'error', message: 'Status konnte nicht geladen werden.' });
    } finally {
      setIsCheckingStatus(false);
      isCheckingStatusRef.current = false;
    }
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (!whatsappConnected) {
      // Poll more frequently (5s) when Facebook popup is open, otherwise every 30s
      const pollInterval = fbPopupOpen ? 5000 : 30000;
      interval = setInterval(handleCheckStatus, pollInterval);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [whatsappConnected, handleCheckStatus, fbPopupOpen]);

  // Detect a "stuck" Facebook popup flow — if no signupData/authCode arrives
  // within 2 minutes of opening the popup, show a recovery UI. This typically
  // happens when Meta interrupts the embedded signup with a 2FA reauth page,
  // which closes the popup before it can postMessage back to us.
  useEffect(() => {
    if (!popupOpenedAt) return;
    if (whatsappConnected) return;
    if (authCode && signupData) return;

    const timer = setTimeout(() => {
      if (!whatsappConnected && !(authCode && signupData)) {
        console.warn('[ConfirmationStep] Facebook popup seems stuck — no auth_code after 2min');
        setPopupStuck(true);
        setStatusMessage({
          type: 'error',
          message:
            'Das Facebook-Popup reagiert nicht mehr. Meta hat den Flow möglicherweise mit einer ' +
            'Sicherheitsabfrage unterbrochen (z.B. 2-Faktor-Authentifizierung). Bitte schließen Sie ' +
            'alle Facebook-Fenster, loggen sich auf facebook.com ein (inkl. 2FA), und klicken dann ' +
            'hier erneut auf "Mit Facebook verbinden".',
        });
      }
    }, 120000); // 2 minutes

    return () => clearTimeout(timer);
  }, [popupOpenedAt, whatsappConnected, authCode, signupData]);

  useEffect(() => {
    let isMounted = true;
    const loadInitial = async () => {
      try {
        await handleCheckStatus();
      } finally {
        if (isMounted) setIsInitialLoading(false);
      }
    };
    loadInitial();
    return () => { isMounted = false; };
  }, [handleCheckStatus]);

  const maskApiKey = (key: string) => (!key ? '' : '*'.repeat(key.length));

  const handlePasswordSubmit = async () => {
    if (!password) { setPasswordError('Bitte geben Sie Ihr Passwort ein.'); return; }
    setIsValidating(true);
    setPasswordError(null);
    try {
      const isValid = await authService.validatePassword(password);
      if (isValid) {
        setShowApiKey(true);
        setPasswordDialogOpen(false);
        setPassword('');
      } else {
        setPasswordError('Ungültiges Passwort.');
      }
    } catch (err) {
      setPasswordError('Fehler bei der Prüfung.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleCopyApiKey = () => {
    if (formData.apiKey) {
      navigator.clipboard.writeText(formData.apiKey);
      setCopyFeedback('Kopiert!');
      setTimeout(() => setCopyFeedback(null), 2000);
    }
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!event.origin.endsWith('facebook.com')) return;
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        console.log('[ConfirmationStep] Facebook postMessage received:', data);
        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          const cleanData = {
            type: 'WA_EMBEDDED_SIGNUP',
            event: String(data.event || 'FINISH'),
            version: '3',
            business_id: String(data.business_id || data.data?.business_id || ''),
            phone_number_id: String(data.phone_number_id || data.data?.phone_number_id || ''),
            waba_id: String(data.waba_id || data.data?.waba_id || '')
          };
          console.log('[ConfirmationStep] WA_EMBEDDED_SIGNUP event:', cleanData);
          setSignupData(cleanData);
          if (['FINISH', 'FINISH_ONLY_WABA', 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'].includes(data.event)) {
            setStatusMessage({ type: 'success', message: 'Facebook-Autorisierung erfolgreich.' });
          } else if (data.event === 'CANCEL') {
            setStatusMessage({ type: 'error', message: 'Onboarding abgebrochen.' });
          }
        }
      } catch (e) { console.error('[ConfirmationStep] postMessage parse error:', e); }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const fbLoginCallback = (response: any) => {
    console.log('[ConfirmationStep] FB.login callback response:', response);
    if (response.authResponse) {
      const code =
        response.authResponse.code ??
        response.authResponse.data?.code ??
        response.authResponse.authorizationCode ??
        response.code;
      console.log('[ConfirmationStep] Extracted auth_code:', code ? `${String(code).slice(0, 10)}...` : 'NONE');
      if (code) {
        setAuthCode(code);
        setStatusMessage({ type: 'info', message: 'Facebook-Autorisierung erfolgreich.' });
      } else {
        setStatusMessage({ type: 'error', message: 'Kein Autorisierungscode von Facebook erhalten. Bitte erneut versuchen.' });
      }
    } else {
      console.warn('[ConfirmationStep] FB.login returned no authResponse:', response);
      setStatusMessage({
        type: 'error',
        message:
          'Facebook-Login nicht abgeschlossen. Das kann passieren, wenn Meta eine ' +
          'Sicherheitsabfrage verlangt (z.B. 2FA). Bitte auf facebook.com einloggen und erneut versuchen.',
      });
    }
  };

  const handleWhatsAppConnect = () => {
    console.log('[ConfirmationStep] Starting FB.login flow');
    setConnectionStatus('connecting');
    setAuthCode(null);
    setSignupData(null);
    setStatusMessage(null);
    setPopupStuck(false);
    autoCompleteTriggeredRef.current = false;
    setFbPopupOpen(true);
    setPopupOpenedAt(Date.now());
    (window as any).FB.login((response: any) => {
      setFbPopupOpen(false);
      fbLoginCallback(response);
    }, {
      config_id: '966700112247031',
      response_type: 'code',
      override_default_response_type: true,
      extras: { setup: {}, featureType: '', sessionInfoVersion: '3' }
    });
  };

  const handleCompleteOnboarding = async (signupDataParam: any, authCodeParam: string) => {
    if (!signupDataParam || !authCodeParam) {
      setStatusMessage({ type: 'info', message: 'Bitte verbinden Sie sich zuerst mit Facebook.' });
      return;
    }
    try {
      setIsCompletingOnboarding(true);
      setConnectionStatus('connecting');
      
      // Errors reject as ApiError with the backend's detail — a 409 (number
      // belongs to another salon) is shown verbatim, see the catch below.
      // For the logged-in salon (token). business_email only for the
      // transition release — see CompleteOnboardingRequest.
      const result = await whatsappService.completeOnboarding({
        auth_code: String(authCodeParam),
        business_email: formData.email,
        signup_data: signupDataParam,
        whatsapp_use_reserved_number: formData.whatsappUseReservedNumber,
      });

      if (result.success) {
        setStatusMessage({ type: 'success', message: 'WhatsApp Business API erfolgreich verbunden.' });
        setConnectionStatus('success');
        setWhatsappConnected(true);
        if (result.facebook_permissions_url) {
          window.open(result.facebook_permissions_url, '_blank');
        }
        handleCheckStatus();
      } else {
        throw new Error(result.detail || result.message || 'Unbekannter Fehler');
      }
    } catch (error: unknown) {
      setStatusMessage({ type: 'error', message: whatsappConnectErrorMessage(error) });
      setConnectionStatus('error');
    } finally {
      setIsCompletingOnboarding(false);
    }
  };

  // Server value wins until the owner actively flips the switch.
  useEffect(() => {
    setWaMode(agentNumbers.whatsappMode);
  }, [agentNumbers.whatsappMode]);

  const handleWhatsAppMode = async (mode: WhatsAppMode) => {
    if (mode === waMode) return;
    const previous = waMode;
    setWaMode(mode);
    try {
      await whatsappService.setMode(mode);
    } catch (error) {
      setWaMode(previous);
      setStatusMessage({
        type: 'error',
        message: 'Einstellung konnte nicht gespeichert werden.',
      });
    }
  };

  const handleFinalizeOnboarding = async () => {
    try {
      setIsFinalizing(true);
      await twilioService.finalizeOnboarding({
        wants_phone_agent: formData.wantsPhoneAgent,
        wants_whatsapp: formData.wantsWhatsApp,
        whatsapp_use_reserved_number: formData.whatsappUseReservedNumber,
      });
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Error finalizing onboarding:', error);
      const detail = error?.message || String(error);
      setStatusMessage({
        type: 'error',
        message: `Fehler beim Abschließen des Onboardings: ${detail}`,
      });
    } finally {
      setIsFinalizing(false);
    }
  };

  const wantsWhatsApp = formData.wantsWhatsApp;
  const whatsappUseReservedNumber = formData.whatsappUseReservedNumber;
  const wantsPhoneAgent = formData.wantsPhoneAgent;
  const showRestOfOnboarding = wantsWhatsApp === true && whatsappUseReservedNumber !== undefined;
  const canFinalize = wantsWhatsApp === false || whatsappConnected;
  const showWhatsAppNumberChoice = wantsWhatsApp === true && (wantsPhoneAgent === true || whatsappUseReservedNumber === undefined);
  const showWhatsAppNumberSummary = wantsWhatsApp === true && wantsPhoneAgent === false && whatsappUseReservedNumber !== undefined;

  useEffect(() => {
    if (autoCompleteTriggeredRef.current) return;
    if (!showRestOfOnboarding) return;
    if (!authCode || !signupData) return;
    if (isCompletingOnboarding || whatsappConnected) return;

    autoCompleteTriggeredRef.current = true;
    setStatusMessage({ type: 'info', message: 'Onboarding wird automatisch abgeschlossen...' });
    handleCompleteOnboarding(signupData, authCode);
  }, [showRestOfOnboarding, authCode, signupData, isCompletingOnboarding, whatsappConnected, handleCompleteOnboarding]);

  if (isInitialLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-strong" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-foreground">WhatsApp verbinden</h2>
        <p className="text-muted-foreground mt-2">Bitte prüfen Sie Ihre Angaben und verbinden Sie anschließend Ihren WhatsApp-Account.</p>
      </div>

      {/* Frage 1: Nur diese steht zuerst – darunter erscheint erst bei "Ja" etwas weiteres */}
      <Card className="border-[#1967D2]/30">
        <CardHeader>
          <CardTitle className="text-lg">Möchten Sie den WhatsApp-Agenten aktivieren?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            <Button
              type="button"
              variant={wantsWhatsApp === true ? "default" : "outline"}
              className={cn(
                "flex-1",
                wantsWhatsApp === true && "bg-[#1967D2] hover:bg-[#1558b0]"
              )}
              onClick={() => {
                onFormChange({ wantsWhatsApp: true });
                twilioService.finalizeOnboarding({ wants_whatsapp: true }).catch(() => {});
              }}
            >
              Ja
            </Button>
            <Button
              type="button"
              variant={wantsWhatsApp === false ? "default" : "outline"}
              className={cn(
                "flex-1",
                wantsWhatsApp === false && "bg-[#1967D2] hover:bg-[#1558b0]"
              )}
              onClick={() => {
                onFormChange({ wantsWhatsApp: false });
                twilioService.finalizeOnboarding({ wants_whatsapp: false }).catch(() => {});
              }}
            >
              Nein
            </Button>
          </div>

          {wantsWhatsApp === false && (
            <p className="text-sm text-muted-foreground">
              Sie können den WhatsApp-Agent später jederzeit aktivieren.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Frage 2: Nur sichtbar wenn Frage 1 mit "Ja" beantwortet – Reservierte Nummer vs. eigene Nummer */}
      {showWhatsAppNumberChoice && (
        <Card className="border-[#1967D2]/30">
          <CardHeader>
            <CardTitle className="text-lg">Welche Nummer soll mit WhatsApp verbunden werden?</CardTitle>
            <p className="text-sm text-muted-foreground font-normal mt-1">
              Möchten Sie die reservierte Nummer nutzen oder eine eigene Nummer mit WhatsApp verbinden?
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                type="button"
                variant={whatsappUseReservedNumber === true ? "default" : "outline"}
                className={cn(
                  "flex-1",
                  whatsappUseReservedNumber === true && "bg-[#1967D2] hover:bg-[#1558b0]"
                )}
                onClick={() => {
                  onFormChange({ whatsappUseReservedNumber: true });
                  twilioService.finalizeOnboarding({ whatsapp_use_reserved_number: true }).catch(() => {});
                }}
              >
                Reservierte Nummer
              </Button>
              <Button
                type="button"
                variant={whatsappUseReservedNumber === false ? "default" : "outline"}
                className={cn(
                  "flex-1",
                  whatsappUseReservedNumber === false && "bg-[#1967D2] hover:bg-[#1558b0]"
                )}
                onClick={() => {
                  onFormChange({ whatsappUseReservedNumber: false });
                  twilioService.finalizeOnboarding({ whatsapp_use_reserved_number: false }).catch(() => {});
                }}
              >
                Eigene Nummer
              </Button>
            </div>

            {whatsappUseReservedNumber === false && (
              <Alert className="bg-warning/10 border-warning/25 text-warning-strong">
                <AlertCircle className="h-4 w-4 text-warning-strong" />
                <AlertDescription className="font-medium">
                  Ihre eigene Nummer muss eine frische Nummer sein, die noch nicht mit WhatsApp (Business) verbunden ist.
                </AlertDescription>
              </Alert>
            )}

            <Alert className="bg-primary/10 border-primary/25">
              <Info className="h-4 w-4 text-primary-strong" />
              <AlertDescription className="text-primary-strong text-sm">
                {whatsappUseReservedNumber === true
                  ? `Im WhatsApp-Popup können Sie eine Nummer frei eingeben. Wenn Sie ${formData.twilioPurchasedNumber ? `die Nummer "${formData.twilioPurchasedNumber}"` : 'die reservierte Nummer'} verwenden, erscheint der Code hier im UI. Bei einer anderen Nummer erhalten Sie den Code per SMS/Anruf auf diesem Gerät.`
                  : whatsappUseReservedNumber === false
                    ? 'Im WhatsApp-Popup können Sie eine Nummer frei eingeben. Bei Ihrer eigenen Nummer kommt der Code per SMS/Anruf auf Ihr Gerät. Wenn Sie stattdessen unsere Nummer nutzen, erscheint der Code hier im UI.'
                    : 'Im WhatsApp-Popup geben Sie die gewünschte Nummer ein. Bei der reservierten Nummer erscheint der Bestätigungscode hier in der Anzeige; bei einer eigenen Nummer erhalten Sie ihn per SMS oder Anruf auf dem Gerät, mit dem Sie sich anmelden.'}
              </AlertDescription>
            </Alert>

            {whatsappUseReservedNumber !== undefined && (
              <Alert className="bg-success/10 border-success/25 text-success-strong">
                <CheckCircle2 className="h-4 w-4 text-success-strong" />
                <AlertDescription className="font-medium">
                  Alles klar. Die Verknüpfung erfolgt hier in diesem Schritt – unten können Sie fortfahren.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {showWhatsAppNumberSummary && (
        <Card className="border-[#1967D2]/30">
          <CardHeader>
            <CardTitle className="text-lg">WhatsApp-Nummer</CardTitle>
            <p className="text-sm text-muted-foreground font-normal mt-1">
              Auswahl aus dem Schritt „Telefon-Agent"
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-sm text-muted-foreground">
              Gewählt: <strong>{whatsappUseReservedNumber ? 'Reservierte Nummer' : 'Eigene Nummer'}</strong>
            </div>
            <Alert className="bg-primary/10 border-primary/25">
              <Info className="h-4 w-4 text-primary-strong" />
              <AlertDescription className="text-primary-strong text-sm">
                Im WhatsApp-Popup können Sie die Rufnummer frei wählen. Bei einer hier aktivierten Nummer erscheint der Verifizierungscode nach 30 Sekunden im nächsten Schritt. Bei Ihrer eigenen Rufnummer erscheint der Code per SMS/Anruf auf ihrem Gerät.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Alles darunter (Zusammenfassung, Verification, Connect) erst wenn beide Fragen beantwortet und WhatsApp = Ja */}
      {showRestOfOnboarding && (
        <>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Zusammenfassung</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* API Key */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-muted-foreground font-medium">
              <Key className="h-4 w-4 text-[#1967D2]" />
              API-Schlüssel
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  value={showApiKey ? formData.apiKey : maskApiKey(formData.apiKey)}
                  disabled
                  className="bg-muted pr-10 font-mono text-sm"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-10 w-10 text-muted-foreground hover:text-foreground"
                  onClick={() => showApiKey ? setShowApiKey(false) : setPasswordDialogOpen(true)}
                >
                  {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {showApiKey && (
                <Button variant="outline" size="icon" onClick={handleCopyApiKey}>
                  {copyFeedback ? <CheckCircle2 className="h-4 w-4 text-success-strong" /> : <Copy className="h-4 w-4" />}
                </Button>
              )}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Building className="h-4 w-4 text-[#1967D2]" /> Unternehmen
              </div>
              <p className="text-sm text-muted-foreground pl-6">{formData.companyName}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <User className="h-4 w-4 text-[#1967D2]" /> Ansprechpartner
              </div>
              <p className="text-sm text-muted-foreground pl-6">{formData.contactPerson}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Mail className="h-4 w-4 text-[#1967D2]" /> E-Mail
              </div>
              <p className="text-sm text-muted-foreground pl-6">{formData.email}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Phone className="h-4 w-4 text-[#1967D2]" /> Telefon
              </div>
              <p className="text-sm text-muted-foreground pl-6">{formData.phone}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Phone className="h-4 w-4 text-[#1967D2]" /> Telefonnummer
              </div>
              <p className="text-sm text-muted-foreground pl-6">
                {plannedWhatsappNumber || 'Nicht gewählt'}
              </p>
            </div>
          </div>

          <div className="pt-4 border-t border-border">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className={cn("h-4 w-4", formData.hasAcceptedTerms ? "text-success-strong" : "text-muted-foreground/70")} />
              <span>Vertragsbedingungen akzeptiert: <strong>{formData.hasAcceptedTerms ? 'Ja' : 'Nein'}</strong></span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* WhatsApp Verification (SMS/Anruf) nur bei reservierter Nummer */}
      {showRestOfOnboarding && formData.whatsappUseReservedNumber === true && formData.wantsTwilioNumber && formData.twilioPurchasedNumber && !whatsappConnected && (
        <WhatsAppVerification
          twilioPurchasedNumber={formData.twilioPurchasedNumber}
          numberType={formData.twilioNumberType || 'local'}
          onCodeReceived={handleVerificationCodeReceived}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            WhatsApp Business API Verbindung
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">

          {/* Pre-connect hint: Facebook 2FA warning */}
          {!whatsappConnected && !fbPopupOpen && !popupStuck && (
            <Alert className="bg-warning/10 border-warning/25 text-warning-strong">
              <Info className="h-4 w-4 text-warning-strong" />
              <AlertDescription className="text-sm">
                <strong>Tipp vor dem Klicken:</strong> Stellen Sie sicher, dass Sie auf{' '}
                <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">
                  facebook.com
                </a>{' '}
                bereits eingeloggt sind und eine eventuelle 2-Faktor-Authentifizierung bereits bestätigt haben.
                Andernfalls kann Meta den Verbindungsvorgang unterbrechen.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="outline"
              onClick={onBack}
              className="border-border"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Zurück
            </Button>

            <Button
              onClick={handleWhatsAppConnect}
              disabled={whatsappConnected || isCheckingStatus}
              className={cn(
                "min-w-[200px] text-white",
                whatsappConnected ? "bg-success hover:bg-success/90" : "bg-[#1877F2] hover:bg-[#166fe5]"
              )}
            >
              {isCheckingStatus ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : whatsappConnected ? (
                <CheckCircle2 className="h-4 w-4 mr-2" />
              ) : (
                <WhatsAppIcon className="h-4 w-4 mr-2" />
              )}
              {whatsappConnected ? 'Verbunden' : (popupStuck ? 'Erneut mit Facebook verbinden' : 'Mit Facebook verbinden')}
            </Button>

            {!whatsappConnected && (popupOpenedAt || popupStuck) && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  console.log('[ConfirmationStep] Manual status check triggered');
                  handleCheckStatus();
                }}
                disabled={isCheckingStatus}
                className="border-border"
              >
                {isCheckingStatus ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                )}
                Status jetzt prüfen
              </Button>
            )}
          </div>

          {/* Multi-location tenants decide here whether one WhatsApp line serves
              every salon or each location gets its own. Hidden for single-salon
              accounts — the question would only lengthen the onboarding. */}
          {whatsappConnected && agentNumbers.sites.length > 1 && (
            <div className="space-y-4 rounded-lg border border-border p-4">
              <div className="space-y-1">
                <Label className="text-base font-medium">
                  Wie soll WhatsApp laufen?
                </Label>
                <p className="text-sm text-muted-foreground">
                  Bei mehreren Standorten kann eine Nummer alle Salons bedienen — oder
                  jeder Standort bekommt seine eigene WhatsApp-Nummer, genau wie beim
                  Telefonagenten.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                {([
                  { id: 'central' as const, label: 'Eine Nummer für alle Salons' },
                  { id: 'per_site' as const, label: 'Eigene Nummer je Standort' },
                ]).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => handleWhatsAppMode(opt.id)}
                    className={cn(
                      'text-sm px-3 py-1.5 rounded-full border transition-colors',
                      waMode === opt.id
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-background text-muted-foreground hover:border-primary hover:text-foreground',
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {waMode === 'per_site' && (
                <AgentNumberManager
                  channel="whatsapp"
                  numbers={agentNumbers.numbers}
                  sites={agentNumbers.sites}
                  loading={agentNumbers.loading}
                  drafts={agentNumbers.drafts}
                  onDraftsChange={agentNumbers.setDrafts}
                  showWhatsAppConnect
                  onReload={agentNumbers.reload}
                />
              )}
            </div>
          )}

          {whatsappConnected && (
            <div className="flex justify-center pt-2">
              <Button
                onClick={handleFinalizeOnboarding}
                disabled={isFinalizing}
                className="bg-success text-success-foreground hover:bg-success/90 min-w-[200px]"
              >
                {isFinalizing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Abschließen
              </Button>
            </div>
          )}

          {!whatsappConnected && !fbPopupOpen && !popupStuck && (
            <p className="text-sm text-muted-foreground text-center">
              Onboarding wird automatisch abgeschlossen, sobald die Facebook-Verbindung fertig ist.
            </p>
          )}

          {/* Recovery UI: popup appears to be stuck */}
          {popupStuck && !whatsappConnected && (
            <Alert className="bg-destructive/10 border-destructive/30 text-destructive-strong">
              <AlertCircle className="h-4 w-4 text-destructive-strong" />
              <AlertDescription className="text-sm space-y-2">
                <p>
                  <strong>Facebook-Popup scheint hängen geblieben.</strong> Das passiert meistens,
                  wenn Meta den Vorgang mit einer Sicherheitsabfrage (2FA) unterbricht.
                </p>
                <p className="font-medium">So kommen Sie weiter:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Öffnen Sie einen neuen Tab und gehen Sie auf <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" className="underline">facebook.com</a></li>
                  <li>Loggen Sie sich dort ein und bestätigen Sie die 2-Faktor-Authentifizierung</li>
                  <li>Kommen Sie zurück zu diesem Fenster</li>
                  <li>Klicken Sie unten auf <strong>"Erneut mit Facebook verbinden"</strong></li>
                </ol>
                <p className="text-xs text-destructive-strong pt-1">
                  Falls Sie das Facebook-Onboarding bereits erfolgreich abgeschlossen haben, klicken Sie auf "Status jetzt prüfen" — wir schauen dann nach ob die Verbindung im Hintergrund angekommen ist.
                </p>
              </AlertDescription>
            </Alert>
          )}

          {fbPopupOpen && !whatsappConnected && (
            <div className="bg-primary/10 border border-primary/25 rounded-lg p-4 text-center space-y-2">
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-primary-strong" />
                <span className="text-sm font-medium text-primary-strong">Facebook-Popup ist geöffnet...</span>
              </div>
              <p className="text-xs text-primary-strong">
                Nehmen Sie sich die Zeit, die Sie brauchen. Wir prüfen alle 5 Sekunden automatisch den Status.
              </p>
              <p className="text-xs text-primary-strong">
                Falls Meta eine 2-Faktor-Abfrage verlangt, wechselt der Flow auf eine neue Facebook-Seite.
                Schließen Sie diese Abfrage ab und klicken dann unten auf <strong>"Popup geschlossen? Status prüfen"</strong>.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  console.log('[ConfirmationStep] User clicked Popup-geschlossen Status prüfen');
                  setFbPopupOpen(false);
                  handleCheckStatus();
                }}
                className="text-xs border-primary/25 text-primary-strong hover:bg-primary/15"
              >
                Popup geschlossen? Status prüfen
              </Button>
            </div>
          )}

          {statusMessage && (
            <Alert className={cn(
              statusMessage.type === 'success' ? "bg-success/10 border-success/25 text-success-strong" :
              statusMessage.type === 'error' ? "bg-destructive/10 border-destructive/30 text-destructive-strong" :
              "bg-primary/10 border-primary/25 text-primary-strong"
            )}>
              {statusMessage.type === 'success' ? <CheckCircle2 className="h-4 w-4" /> :
               statusMessage.type === 'error' ? <AlertCircle className="h-4 w-4" /> :
               <AlertCircle className="h-4 w-4" />}
              <AlertDescription>{statusMessage.message}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
        </>
      )}

      {/* Bei "Nein" oder bevor zweite Frage beantwortet: Zurück + ggf. Abschließen */}
      {!showRestOfOnboarding && (
        <div className="flex justify-between">
          <Button variant="outline" onClick={onBack} className="border-border">
            <ArrowLeft className="mr-2 h-4 w-4" /> Zurück
          </Button>
          {wantsWhatsApp === false && (
            <Button
              onClick={handleFinalizeOnboarding}
              disabled={isFinalizing}
              className="bg-success text-success-foreground hover:bg-success/90"
            >
              {isFinalizing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Abschließen
            </Button>
          )}
        </div>
      )}

      <Dialog open={openSuccessDialog} onOpenChange={setOpenSuccessDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Verbindung erfolgreich</DialogTitle>
            <DialogDescription>
              Ihr WhatsApp Business Account wurde erfolgreich verbunden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setOpenSuccessDialog(false)}>Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Passwort erforderlich</DialogTitle>
            <DialogDescription>Bitte geben Sie Ihr Passwort ein.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Passwort</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setPasswordError(null); }}
              onKeyDown={(e) => e.key === 'Enter' && !isValidating && handlePasswordSubmit()}
            />
            {passwordError && <p className="text-sm text-destructive-strong">{passwordError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handlePasswordSubmit} disabled={isValidating}>
              {isValidating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Bestätigen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfirmationStep;
